import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Camera, Settings, Images, ArrowRight, Sparkles } from 'lucide-react'
import { motion } from 'framer-motion'
import { useEventStore } from '@/store'

export default function HubPage() {
  const [hovered, setHovered] = useState<number | null>(null)
  const events = useEventStore((s) => s.events)
  const loadEvents = useEventStore((s) => s.loadEvents)

  useEffect(() => {
    loadEvents()
  }, [loadEvents])

  const cards = [
    {
      icon: Camera,
      title: 'Events',
      subtitle: `${events.length} event${events.length !== 1 ? 's' : ''}`,
      description: 'Create, edit, and launch photo booth events.',
      href: '/admin',
      color: 'from-blue-500 to-indigo-600',
      bg: 'bg-blue-500/10',
    },
    {
      icon: Settings,
      title: 'Admin',
      subtitle: 'Operator Panel',
      description: 'Configure cameras, layouts, effects, and sharing.',
      href: '/admin',
      color: 'from-emerald-500 to-teal-600',
      bg: 'bg-emerald-500/10',
    },
    {
      icon: Images,
      title: 'Gallery',
      subtitle: 'Recap',
      description: 'Browse, download, and manage all captured sessions.',
      href: '/admin/gallery',
      color: 'from-rose-500 to-pink-600',
      bg: 'bg-rose-500/10',
    },
  ]

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-[#0a0a0f] text-white">
      {/* Animated background blobs */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <motion.div
          animate={{ x: [0, 60, 0], y: [0, -40, 0], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute -left-20 -top-20 h-[500px] w-[500px] rounded-full bg-blue-600/20 blur-[120px]"
        />
        <motion.div
          animate={{ x: [0, -50, 0], y: [0, 60, 0], opacity: [0.2, 0.4, 0.2] }}
          transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute -right-20 bottom-0 h-[600px] w-[600px] rounded-full bg-purple-600/20 blur-[120px]"
        />
        <motion.div
          animate={{ scale: [1, 1.2, 1], opacity: [0.15, 0.25, 0.15] }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute left-1/3 top-1/3 h-[400px] w-[400px] rounded-full bg-emerald-600/15 blur-[100px]"
        />
      </div>

      {/* Grid pattern overlay */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
          backgroundSize: '60px 60px',
        }}
      />

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center px-6">
        {/* Logo */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-4 flex items-center gap-3"
        >
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 shadow-lg shadow-blue-500/25">
            <Camera className="h-6 w-6 text-white" />
          </div>
          <h1 className="text-4xl font-bold tracking-tight">
            Booth<span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">Flow</span>
          </h1>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="mb-12 text-center text-lg text-white/40"
        >
          Professional Photo Booth Software
        </motion.p>

        {/* Cards */}
        <div className="grid w-full max-w-4xl gap-5 md:grid-cols-3">
          {cards.map((card, i) => (
            <motion.div
              key={card.title}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 + i * 0.15, duration: 0.6 }}
            >
              <Link
                to={card.href}
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
                className="group relative flex flex-col rounded-2xl border border-white/5 bg-white/[0.03] p-6 backdrop-blur-sm transition-all duration-300 hover:border-white/10 hover:bg-white/[0.06]"
              >
                {/* Glow on hover */}
                <div
                  className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${card.color} opacity-0 blur-xl transition-opacity duration-500 group-hover:opacity-20`}
                />

                <div className="relative z-10">
                  <div className={`mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl ${card.bg}`}>
                    <card.icon className="h-6 w-6 text-white" />
                  </div>

                  <div className="mb-1 flex items-center gap-2">
                    <h2 className="text-xl font-semibold">{card.title}</h2>
                    {i === 0 && (
                      <span className="flex h-5 items-center rounded-full bg-blue-500/20 px-2 text-[10px] font-medium text-blue-300">
                        <Sparkles className="mr-1 h-3 w-3" />
                        {card.subtitle}
                      </span>
                    )}
                    {i !== 0 && (
                      <span className="text-xs text-white/30">{card.subtitle}</span>
                    )}
                  </div>

                  <p className="mb-6 text-sm leading-relaxed text-white/40">{card.description}</p>

                  <div className="mt-auto flex items-center gap-1 text-sm font-medium text-white/60 transition-colors group-hover:text-white">
                    Enter
                    <ArrowRight className={`h-4 w-4 transition-transform duration-300 ${hovered === i ? 'translate-x-1' : ''}`} />
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="mt-16 text-xs text-white/20"
        >
          PoseBox v1.0.4 &middot; Built for professionals
        </motion.div>
      </div>
    </div>
  )
}
